import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Box, Button, Checkbox, Flex, FormControl, FormLabel, HStack, IconButton, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Spinner, Table,
  Tbody, Td, Text, Textarea, Th, Thead, Tooltip, Tr, useDisclosure, useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaEnvelope, FaExchangeAlt, FaFilePdf, FaRegTrashAlt } from "react-icons/fa";
import api from "../../../services/api";
import { crm, Pill } from "../../../theme";
import SectionCard from "./SectionCard";
import { brl, toneColors } from "./duimp";
import { tdProps, thProps } from "./tableStyles";
import { ExchangeNumeraryPanel as PanelData, ExchangeNumeraryRequest } from "../../../models/ExchangeNumerary";
import {
  createExchangeNumerary,
  deleteExchangeNumerary,
  downloadExchangeNumeraryPdf,
  getExchangeNumeraryPanel,
  sendExchangeNumerary,
  setExchangeNumeraryReceived,
} from "../../../services/exchangeNumeraryService";

const parseNumber = (value: string): number | null => {
  const text = value.trim();
  if (!text) return null;
  const normalized = text.includes(",") ? text.replace(/\./g, "").replace(",", ".") : text;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};
const money = (value?: number | null, digits = 2) =>
  value == null ? "—" : value.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });
const date = (value?: string | null) => (value ? DateTime.fromISO(value).toFormat("dd/LL/yyyy") : "—");
const toDraft = (value?: number | null) => (value == null ? "" : String(value).replace(".", ","));

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

const LABELS = ["ANTECIPAÇÃO", "SALDO", "PAGAMENTO"];

/**
 * Solicitação de numerário de câmbio ao cliente (encomenda): escolhe parcelas abertas da invoice, informa a taxa,
 * gera o PDF no modelo da CVF, envia por e-mail e o financeiro marca quando o cliente pagou.
 */
const ExchangeNumeraryPanel = ({ processId }: { processId: string }) => {
  const toast = useToast();
  const [data, setData] = useState<PanelData>();
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [rate, setRate] = useState("");
  const [label, setLabel] = useState("");
  const [processLabel, setProcessLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const emit = useDisclosure();
  const send = useDisclosure();
  const [sending, setSending] = useState<ExchangeNumeraryRequest | null>(null);
  const [mail, setMail] = useState({ to: "", cc: "", message: "" });
  const [toDelete, setToDelete] = useState<ExchangeNumeraryRequest | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [rates, setRates] = useState<Record<string, number>>({});

  const load = async () => {
    try {
      setData(await getExchangeNumeraryPanel(processId));
    } catch {
      toast({ status: "error", title: "Não foi possível carregar o numerário de câmbio" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/api/process/currencies")
      .then(({ data: c }) => setRates({ USD: c.dollarBr, EUR: c.euroBr, CNY: c.yuenBr }))
      .catch(() => undefined);
  }, [processId]);

  const open = data?.installments.filter((i) => !i.paid) ?? [];

  const openEmit = () => {
    const firstOpen = open.find((i) => !i.requestedAt) ?? open[0];
    setSelected(firstOpen ? { [firstOpen.financialId]: toDraft(firstOpen.amount) } : {});
    const current = rates[data?.currency ?? "USD"];
    setRate(current ? current.toFixed(4).replace(".", ",") : "");
    setLabel("");
    setProcessLabel(data?.defaultProcessLabel ?? "");
    emit.onOpen();
  };

  const lines = Object.entries(selected).map(([financialId, amount]) => ({ financialId, amount: parseNumber(amount) }));
  const rateValue = parseNumber(rate) ?? 0;
  // Igual ao servidor: valor com 2 casas × taxa, arredondado por linha
  const round2 = (v: number) => Math.round(v * 100) / 100;
  const total = lines.reduce((sum, l) => sum + round2(round2(l.amount ?? 0) * rateValue), 0);
  const autoLabel = useMemo(() => {
    const numbers = Object.keys(selected).map((id) => data?.installments.find((i) => i.financialId === id)?.number);
    return numbers.length && numbers.every((n) => n === 1) ? "ANTECIPAÇÃO" : "SALDO";
  }, [selected, data]);

  const create = async () => {
    setSaving(true);
    try {
      const result = await createExchangeNumerary(processId, {
        processLabel: processLabel.trim() || undefined,
        label: label || autoLabel,
        exchangeRate: rateValue,
        lines,
      });
      if (!result.success) {
        toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível emitir" });
        return;
      }
      emit.onClose();
      toast({ status: "success", title: "Solicitação emitida" });
      await load();
    } catch {
      toast({ status: "error", title: "Não foi possível emitir" });
    } finally {
      setSaving(false);
    }
  };

  const openSend = (request: ExchangeNumeraryRequest) => {
    setSending(request);
    setMail({ to: data?.clientEmails.join("; ") ?? "", cc: "", message: "" });
    send.onOpen();
  };

  const doSend = async () => {
    if (!sending) return;
    setSaving(true);
    try {
      const result = await sendExchangeNumerary(processId, sending.id, mail);
      if (!result.success) {
        toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível enviar" });
        return;
      }
      send.onClose();
      toast({ status: "success", title: "Solicitação enviada por e-mail" });
      await load();
    } catch {
      toast({ status: "error", title: "Não foi possível enviar" });
    } finally {
      setSaving(false);
    }
  };

  const received = async (request: ExchangeNumeraryRequest, value: string | null) => {
    try {
      const result = await setExchangeNumeraryReceived(processId, request.id, value ? `${value}T00:00:00` : null);
      if (!result.success) toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível salvar" });
    } catch {
      toast({ status: "error", title: "Não foi possível salvar o recebimento" });
    }
    await load();
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      const result = await deleteExchangeNumerary(processId, toDelete.id);
      if (!result.success) toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível excluir" });
    } catch {
      toast({ status: "error", title: "Não foi possível excluir" });
    }
    setToDelete(null);
    await load();
  };

  const pdf = (request: ExchangeNumeraryRequest) =>
    downloadExchangeNumeraryPdf(processId, request.id, request.fileName).catch(() =>
      toast({ status: "error", title: "Não foi possível gerar o PDF" }));

  return (
    <SectionCard
      icon={<FaExchangeAlt />}
      title="Numerário de câmbio"
      subtitle="Solicitação ao cliente das parcelas que a CVF fecha com o exportador"
      actions={
        data?.canManage ? (
          <Button size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isDisabled={!open.length} onClick={openEmit}>
            Emitir solicitação
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <Flex justify="center" py="20px"><Spinner size="sm" /></Flex>
      ) : (
        <Box px={{ base: "16px", md: "24px" }} py="16px">
          {!data?.unitConfigured && (
            <Box mb="12px">
              <Pill {...toneColors.warning}>Dados bancários da unidade não cadastrados (Configurações › Dados das unidades): o PDF sai sem banco e o envio fica bloqueado</Pill>
            </Box>
          )}
          <Text fontSize="13px" fontWeight="700" mb="6px">Parcelas da invoice</Text>
          <Box overflowX="auto" mb="18px">
            <Table size="sm">
              <Thead><Tr><Th {...thProps}>Parcela</Th><Th {...thProps}>Vencimento</Th><Th {...thProps} isNumeric>Valor ({data?.currency})</Th><Th {...thProps}>Câmbio</Th><Th {...thProps}>Numerário</Th></Tr></Thead>
              <Tbody>
                {data?.installments.map((i) => (
                  <Tr key={i.financialId}>
                    <Td {...tdProps}>{i.number ? `${i.number}ª` : "—"}</Td>
                    <Td {...tdProps}>{date(i.dueDate)}</Td>
                    <Td {...tdProps} isNumeric>{money(i.amount)}</Td>
                    <Td {...tdProps}>{i.paid ? <Pill {...toneColors.success}>Pago{i.rate ? ` · ${money(i.rate, 4)}` : ""}</Pill> : <Pill {...toneColors.neutral}>Aberto</Pill>}</Td>
                    <Td {...tdProps}>
                      {i.receivedAt ? <Pill {...toneColors.success}>Recebido {date(i.receivedAt)}</Pill>
                        : i.requestedAt ? <Pill {...toneColors.warning}>Solicitado {date(i.requestedAt)}</Pill>
                          : i.paid ? "—" : <Pill {...toneColors.neutral}>Não solicitado</Pill>}
                    </Td>
                  </Tr>
                ))}
                {!data?.installments.length && <Tr><Td {...tdProps} colSpan={5} color={crm.textMuted}>Processo sem parcelas de invoice.</Td></Tr>}
              </Tbody>
            </Table>
          </Box>

          <Text fontSize="13px" fontWeight="700" mb="6px">Solicitações emitidas</Text>
          {data?.requests.length ? (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead><Tr><Th {...thProps}>Emitida</Th><Th {...thProps}>Tipo</Th><Th {...thProps}>Parcelas</Th><Th {...thProps} isNumeric>Taxa</Th><Th {...thProps} isNumeric>Total R$</Th><Th {...thProps}>Enviada</Th><Th {...thProps}>Recebida em</Th><Th {...thProps} /></Tr></Thead>
                <Tbody>
                  {data.requests.map((r) => (
                    <Tr key={r.id}>
                      <Td {...tdProps} whiteSpace="nowrap">{date(r.createdDate)}<Text fontSize="11px" color={crm.textFaint}>{r.issuedByName}</Text></Td>
                      <Td {...tdProps}>{r.label}</Td>
                      <Td {...tdProps}>{r.lines.map((l) => `${l.installmentNumber ?? "—"}ª ${money(l.amount)}`).join(" · ")}</Td>
                      <Td {...tdProps} isNumeric>{money(r.exchangeRate, 4)}</Td>
                      <Td {...tdProps} isNumeric fontWeight="700">{brl(r.totalBrl)}</Td>
                      <Td {...tdProps} maxW="220px">
                        {r.sentAt ? <Tooltip label={r.sentTo}><Text fontSize="12px" noOfLines={1}>{date(r.sentAt)} · {r.sentTo}</Text></Tooltip> : <Text fontSize="12px" color={crm.textMuted}>Não enviada</Text>}
                      </Td>
                      <Td {...tdProps}>
                        <Input
                          key={`${r.id}-${r.receivedAt ?? ""}`}
                          size="xs"
                          type="date"
                          w="130px"
                          defaultValue={r.receivedAt ? r.receivedAt.slice(0, 10) : ""}
                          isDisabled={!data.canManage}
                          onBlur={(e) => {
                            const value = e.target.value || null;
                            if (value !== (r.receivedAt ? r.receivedAt.slice(0, 10) : null)) received(r, value);
                          }}
                          aria-label="Data de recebimento do numerário"
                        />
                        {r.receivedByName && <Text fontSize="11px" color={crm.textFaint}>{r.receivedByName}</Text>}
                      </Td>
                      <Td {...tdProps}>
                        <HStack spacing="2px">
                          <Tooltip label="Baixar PDF"><IconButton size="sm" variant="ghost" aria-label="Baixar PDF" icon={<FaFilePdf />} onClick={() => pdf(r)} /></Tooltip>
                          {data.canManage && (
                            <>
                              <Tooltip label="Enviar por e-mail"><IconButton size="sm" variant="ghost" aria-label="Enviar por e-mail" icon={<FaEnvelope />} onClick={() => openSend(r)} /></Tooltip>
                              <Tooltip label="Excluir"><IconButton size="sm" variant="ghost" color={crm.danger} aria-label="Excluir solicitação" icon={<FaRegTrashAlt />} onClick={() => setToDelete(r)} /></Tooltip>
                            </>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Text fontSize="13px" color={crm.textMuted}>Nenhuma solicitação emitida.</Text>
          )}
        </Box>
      )}

      <Modal isOpen={emit.isOpen} onClose={emit.onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>Solicitação de numerário de câmbio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px" mb="14px">
              <FormControl><Label>Ref. no documento</Label><Input size="sm" value={processLabel} maxLength={40} onChange={(e) => setProcessLabel(e.target.value)} /></FormControl>
              <FormControl>
                <Label>Tipo</Label>
                <Select size="sm" value={label} onChange={(e) => setLabel(e.target.value)}>
                  <option value="">Automático ({autoLabel})</option>
                  {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </Select>
              </FormControl>
              <FormControl isInvalid={rateValue <= 0}>
                <Label>Taxa de câmbio (R$)</Label>
                <Input size="sm" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0,0000" />
              </FormControl>
            </SimpleGrid>
            <Text fontSize="12px" fontWeight="600" color={crm.textMuted} mb="6px">Parcelas a solicitar ({data?.currency})</Text>
            {open.map((i) => {
              const checked = i.financialId in selected;
              return (
                <Flex key={i.financialId} align="center" gap="10px" mb="6px">
                  <Checkbox
                    isChecked={checked}
                    onChange={(e) => setSelected((s) => {
                      const next = { ...s };
                      if (e.target.checked) next[i.financialId] = toDraft(i.amount);
                      else delete next[i.financialId];
                      return next;
                    })}
                  >
                    <Text fontSize="13px" w="170px">{i.number ? `${i.number}ª parcela` : "Parcela"} · venc. {date(i.dueDate)}</Text>
                  </Checkbox>
                  <Input size="sm" w="140px" isDisabled={!checked} value={selected[i.financialId] ?? toDraft(i.amount)}
                    onChange={(e) => setSelected((s) => ({ ...s, [i.financialId]: e.target.value }))} />
                  {i.requestedAt && <Pill {...toneColors.warning}>já solicitada {date(i.requestedAt)}</Pill>}
                </Flex>
              );
            })}
            <Flex justify="space-between" mt="14px" p="10px 12px" bg={crm.subtle} borderRadius="10px">
              <Text fontWeight="700">Total a ser pago</Text>
              <Text fontWeight="700">{brl(total)}</Text>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="8px" onClick={emit.onClose}>Cancelar</Button>
            <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving}
              isDisabled={!lines.length || rateValue <= 0 || lines.some((l) => !l.amount || l.amount <= 0)} onClick={create}>
              Emitir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={send.isOpen} onClose={send.onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader>Enviar solicitação por e-mail</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="13px" color={crm.textMuted} mb="10px">Anexo: {sending?.fileName}</Text>
            <FormControl mb="10px"><Label>Para (separe com ;)</Label><Input size="sm" value={mail.to} onChange={(e) => setMail({ ...mail, to: e.target.value })} /></FormControl>
            <FormControl mb="10px"><Label>Cópia</Label><Input size="sm" value={mail.cc} onChange={(e) => setMail({ ...mail, cc: e.target.value })} /></FormControl>
            <FormControl>
              <Label>Mensagem (opcional)</Label>
              <Textarea size="sm" rows={4} value={mail.message} placeholder={`Segue a solicitação de numerário de câmbio do processo ${sending?.processLabel ?? ""}, no valor de ${brl(sending?.totalBrl ?? 0)}.`}
                onChange={(e) => setMail({ ...mail, message: e.target.value })} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="8px" onClick={send.onClose}>Cancelar</Button>
            <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} isDisabled={!mail.to.trim()} onClick={doSend}>Enviar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={Boolean(toDelete)} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px">
            <AlertDialogHeader>Excluir solicitação?</AlertDialogHeader>
            <AlertDialogBody>A solicitação {toDelete?.label} de {date(toDelete?.createdDate)} ({brl(toDelete?.totalBrl ?? 0)}) sai do histórico.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setToDelete(null)}>Cancelar</Button>
              <Button colorScheme="red" ml="8px" onClick={remove}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </SectionCard>
  );
};

export default ExchangeNumeraryPanel;
